import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/* ── İkon bileşeni (mevcut SVG path'lerle uyumlu) ── */
const Icon = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const icons = {
  dashboard: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10',
  customers: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 11a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75',
  devices:   'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18',
  users:     'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 11a4 4 0 100-8 4 4 0 000 8z',
  reports:   'M18 20V10 M12 20V4 M6 20v-6',
  alerts:    'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01',
  logout:    'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4 M16 17l5-5-5-5 M21 12H9',
  settings:  'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z',
};

const adminNav = [
  { to:'/admin',           label:'Genel Bakış', icon:'dashboard', end:true },
  { to:'/admin/customers', label:'Müşteriler',  icon:'customers' },
  { to:'/admin/devices',   label:'Cihazlar',    icon:'devices'   },
  { to:'/admin/users',     label:'Kullanıcılar',icon:'users'     },
];

const customerNav = [
  { to:'/dashboard',         label:'Dashboard', icon:'dashboard', end:true },
  { to:'/dashboard/reports', label:'Raporlar',  icon:'reports'   },
  { to:'/dashboard/alarms',  label:'Alarmlar',  icon:'alerts'    },
  { to:'/dashboard/devices', label:'Cihazlar',  icon:'devices'   },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const navItems  = isAdmin ? adminNav : customerNav;
  const handleLogout = () => { logout(); navigate('/login'); };

  const isStore = user?.dashboard_mode === 'store_group';

  /* ── Renk modunu body üzerinden CSS'e bildir ── */
  useEffect(() => {
    if (!isAdmin) {
      document.body.dataset.mode = isStore ? 'store' : 'mall';
    }
    return () => { delete document.body.dataset.mode; };
  }, [isStore, isAdmin]);

  /* ── Reports sayfasında sidebar'ın kendi içeriği olduğu için
        Layout içerik padding'ini kaldır (full-bleed) ── */
  const isFullBleed = location.pathname === '/dashboard/reports';

  /* ── Alarmlar badge sayısı (basit state — gelecekte API'dan alınabilir) ── */
  const [alarmCount, setAlarmCount] = useState(0);
  useEffect(() => {
    if (isAdmin) return;
    const fn = () => {
      import('../api/client').then(({ default: api }) => {
        api.get('/reports/alerts').then(r => {
          const active = (r.data.data?.alarms || []).filter(a => a.status !== 'resolved');
          setAlarmCount(active.length);
        }).catch(() => {});
      });
    };
    fn();
    const iv = setInterval(fn, 60000);
    return () => clearInterval(iv);
  }, [isAdmin]);

  /* ── Marka bilgisi ── */
  const modeLabel = isStore ? 'Şube Zinciri' : 'AVM';

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'var(--bg)', flexDirection:'column' }}>

      {/* ══ Üst bar ══ */}
      {!isAdmin && (
        <header style={{
          height: 'var(--topbar-height)',
          background: 'var(--topbar-bg)',
          borderBottom: '1px solid var(--topbar-border)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          gap: 14,
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}>
          {/* Logo */}
          <div style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:14, color:'var(--text)', whiteSpace:'nowrap' }}>
            AVM<span style={{ color:'var(--accent2)' }}>Analitik</span>
          </div>

          {/* Sayfa navigasyonu */}
          <nav style={{ display:'flex', gap:1, marginLeft:6 }}>
            {customerNav.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 11px', borderRadius: 'var(--r-sm)',
                  fontSize: 12.5, fontWeight: 500, textDecoration: 'none',
                  color: isActive ? 'var(--text)' : 'var(--text-muted)',
                  background: isActive ? 'var(--surface)' : 'transparent',
                  transition: 'all 0.15s', whiteSpace: 'nowrap',
                  position: 'relative',
                })}>
                {item.label}
                {item.icon === 'alerts' && alarmCount > 0 && (
                  <span style={{
                    fontSize: 9, background: 'var(--red)', color: '#fff',
                    padding: '1px 5px', borderRadius: 8, fontWeight: 600,
                  }}>{alarmCount}</span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Sağ: mod badge + kullanıcı */}
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:12 }}>
            <span className="badge badge-mode">{modeLabel}</span>
            <span style={{ fontSize:11, color:'var(--text3)' }}>{user?.name === 'Mustafa' ? 'Mustafa Aydın' : (user?.full_name || user?.fullName || user?.name || user?.email)}</span>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'var(--accent-bg)', color: 'var(--accent2)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontWeight: 700, fontSize: 11, flexShrink: 0,
            }}>
              {(user?.full_name || user?.fullName || user?.name || user?.email || '?')?.[0]?.toUpperCase()}
            </div>
            <button onClick={handleLogout}
              style={{ background:'transparent', color:'var(--text3)', padding:'4px 8px', borderRadius:'var(--r-sm)', fontSize:12, fontWeight:500, border:'1px solid var(--border)' }}>
              Çıkış
            </button>
          </div>
        </header>
      )}

      {/* ══ Gövde: sol sidebar + içerik ══ */}
      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

        {/* ── Sol sidebar ── */}
        {!(isFullBleed && !isAdmin) && (
        <aside style={{
          background: isAdmin ? 'var(--bg2)' : 'var(--sb-bg)',
          borderRight: `1px solid ${isAdmin ? 'var(--border)' : 'var(--sb-border)'}`,
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: isAdmin ? 0 : 'var(--topbar-height)',
          height: isAdmin ? '100vh' : `calc(100vh - var(--topbar-height))`,
          transition: 'width 0.2s',
          overflow: 'hidden',
          flexShrink: 0,
          width: collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)',
        }}>

          {/* Logo (sadece admin veya topbar yoksa) */}
          {isAdmin && (
            <div style={{ padding:'18px 14px 12px' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                {!collapsed && (
                  <div>
                    <div style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:14, color:'var(--text)' }}>AVM Analitik</div>
                    <div style={{ fontSize:10, color:'var(--accent2)', letterSpacing:'0.06em', textTransform:'uppercase' }}>Admin Panel</div>
                  </div>
                )}
                <button onClick={() => setCollapsed(c => !c)}
                  style={{ background:'var(--surface)', border:'1px solid var(--border)', color:'var(--text3)', padding:5, borderRadius:'var(--r-sm)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Icon d={collapsed ? 'M13 17l5-5-5-5 M6 17l5-5-5-5' : 'M11 17l-5-5 5-5 M18 17l-5-5 5-5'} size={15} />
                </button>
              </div>
            </div>
          )}

          {/* Admin sidebar nav (dikey) */}
          {isAdmin && (
            <nav style={{ flex:1, padding:'4px 8px', display:'flex', flexDirection:'column', gap:2 }}>
              {navItems.map(item => (
                <NavLink key={item.to} to={item.to} end={item.end}
                  style={({ isActive }) => ({
                    display:'flex', alignItems:'center', gap:10,
                    padding:'9px 10px', borderRadius:'var(--r-sm)',
                    color: isActive ? 'var(--accent2)' : 'var(--text2)',
                    fontSize:13.5, fontWeight:500, transition:'all 0.15s', whiteSpace:'nowrap',
                    background: isActive ? 'var(--accent-dim)' : 'transparent',
                    textDecoration:'none',
                  })}>
                  <Icon d={icons[item.icon]} size={17} />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </nav>
          )}

          {/* Customer sidebar nav — Reports harici sayfalar için (full sidebar değil, sadece admin nav için) */}
          {/* Customer sol sidebar'ı yok — üst nav kullanıyor.
              Reports sayfası kendi iç sidebar'ını yönetiyor.
              Diğer customer sayfaları (Dashboard, Devices, Alarms) üst nav ile navigate ediyor. */}

          {/* Alt: kullanıcı bilgisi */}
          <div style={{ padding:'10px 8px', borderTop:'1px solid var(--border)', display:'flex', flexDirection:'column', gap:6 }}>
            {!collapsed && (
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:'var(--accent-dim)', color:'var(--accent2)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:12, flexShrink:0 }}>
                  {(user?.full_name || user?.fullName || user?.name || user?.email || '?')?.[0]?.toUpperCase()}
                </div>
                {!collapsed && (
                  <div style={{ overflow:'hidden' }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:120 }}>{user?.name === 'Mustafa' ? 'Mustafa Aydın' : (user?.full_name || user?.fullName || user?.name || user?.email)}</div>
                    <div style={{ fontSize:10, color:'var(--text3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:120 }}>{user?.email}</div>
                  </div>
                )}
              </div>
            )}
            <button onClick={handleLogout}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderRadius:'var(--r-sm)', color:'var(--text3)', background:'transparent', fontSize:12.5, fontWeight:500 }}>
              <Icon d={icons.logout} size={15} />
              {!collapsed && <span>Çıkış</span>}
            </button>
          </div>
        </aside>
        )}

        {/* ── Ana içerik ── */}
        <main style={{ flex:1, overflow:'auto', background:'var(--bg)' }}>
          {isFullBleed ? (
            /* Reports sayfası: padding yok, tam genişlik */
            <div style={{ height:'100%' }}>{children}</div>
          ) : (
            /* Diğer sayfalar: normal padding */
            <div style={{ padding:'var(--content-padding)', maxWidth:'var(--content-max)', margin:'0 auto' }}>
              {children}
            </div>
          )}
        </main>

      </div>
    </div>
  );
}
